/**
 * Authentication Context
 *
 * Provides admin authentication state, backed by Supabase Auth. Session
 * persistence and token refresh are handled by the Supabase client; this
 * context mirrors the GoTrue session into React state and resolves the
 * `admin_users` profile via authService.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import {
  authService,
  type AdminUser,
  type LoginCredentials,
  type LoginResponse,
} from "@/services/auth/authService";
import { logger } from "@/utils/logger";

interface AuthContextType {
  // State
  user: AdminUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;

  // Utilities
  isSuperAdmin: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Hydrate from any persisted Supabase session on mount.
    authService
      .getCurrentUser()
      .then((u) => {
        if (mounted) setUser(u);
      })
      .catch((error) => logger.error("Error initializing auth", { error }))
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    // Stay in sync with sign-in / sign-out / token refresh. Supabase's
    // autoRefreshToken keeps the session alive — no manual interval needed.
    const unsubscribe = authService.onAuthStateChange((u) => {
      if (mounted) setUser(u);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleLogin = async (
    credentials: LoginCredentials,
  ): Promise<LoginResponse> => {
    try {
      const response = await authService.login(credentials);
      if (response.success && response.user) {
        setUser(response.user);
        logger.info("User logged in", { userId: response.user.id });
      }
      return response;
    } catch (error) {
      logger.error("Login error in context", { error });
      return { success: false, message: "Login failed. Please try again." };
    }
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await authService.logout();
      setUser(null);
      logger.info("User logged out");
    } catch (error) {
      logger.error("Logout error in context", { error });
    }
  };

  const value: AuthContextType = {
    // State
    user,
    isAuthenticated: !!user,
    isLoading,

    // Actions
    login: handleLogin,
    logout: handleLogout,

    // Utilities
    isSuperAdmin: () => user?.isSuperAdmin ?? false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use authentication context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

/**
 * Hook to require authentication
 */
export const useRequireAuth = (): AuthContextType => {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      window.location.href = "/admin/login";
    }
  }, [auth.isAuthenticated, auth.isLoading]);

  return auth;
};
